
import {
  defineComponent, watch, computed, ref,
} from 'vue';
import { useRouter } from 'vue-router';
import { Swiper, SwiperSlide } from 'swiper/vue/swiper-vue';
import ItemCase from '@/modules/drive/components/items/ItemCase.vue';
import AppItemWrapper from '@/components/AppItemWrapper.vue';
import {
  ICase, IData, IList, IMixedData, IResponse,
} from '@/types/interfaces';
import { useMainStore } from '@/store/mainStore';
import { useDriveStore } from '../store/driveStore';
import AppSkeletonCase from '@/components/skeletons/AppSkeletonCase.vue';
import UsePagination from '@/composables/UsePagination';
import { DIRECTION } from '@/helpers/isScrollEnd';
import DriveService from '../services/DriveService';
import 'swiper/swiper-bundle.css';

export default defineComponent({
  components: {
    ItemCase,
    AppItemWrapper,
    AppSkeletonCase,
    Swiper,
    SwiperSlide,
  },
  props: {
    title: { type: String, required: true },
    isNetwork: { type: Boolean, default: false },
    skeleton: { type: Boolean, default: false },
    showCreateCaseBtn: { type: Boolean, default: false },
  },
  setup(props) {
    const router = useRouter();
    const mainStore = useMainStore();
    const driveStore = useDriveStore();

    const {
      scroller, pageIdx, isPageLoading, onScroll, setLastPage, onCacheScroll,
    } = UsePagination();

    const dataLoading = ref(false);
    const searchCases = ref<ICase[]>([]);

    const casesLastPage = computed(() => (props.isNetwork ? driveStore.networkCases.lastPage : driveStore.cases.lastPage));
    const casesCurrentPage = computed(() => (props.isNetwork ? driveStore.networkCases.page : driveStore.cases.page));

    const cases = computed(() => {
      if (driveStore.search) {
        return searchCases.value.length > 50 ? searchCases.value.slice(0, 50) : searchCases.value;
      }
      const data = props.isNetwork ? driveStore.networkCases.data : driveStore.cases.data;
      return data.length > 50 ? data.slice(0, 50) : data;
    });

    function fetchCacheData() {
      if (props.isNetwork) {
        return driveStore.fetchNetworkCases();
      }
      return driveStore.fetchCases();
    }

    async function fetchSearchData() {
      const currentSearch = driveStore.search;
      if (props.isNetwork) {
        return DriveService.getNetworkCases(pageIdx.value, driveStore.search)
          .then((res) => {
            let casesResponse: any = { ...res };
            casesResponse.results.data.items = casesResponse.results.data.items.map((item: IMixedData) => ({ ...item.case as ICase, sharedTime: item.shared }));

            casesResponse = casesResponse as IResponse<IData<IList<ICase>>>;

            if (pageIdx.value === 0) {
              searchCases.value = casesResponse.results.data.items;
            } else {
              searchCases.value.push(...casesResponse.results.data.items);
            }

            return casesResponse;
          });
      }
      return DriveService.getCases(pageIdx.value, driveStore.search)
        .then((response) => {
          if (currentSearch !== driveStore.search) {
            return Promise.reject(new Error('response is out of date'));
          }
          if (pageIdx.value === 0) {
            searchCases.value = response.results.data.items;
          } else {
            searchCases.value.push(...response.results.data.items);
          }
          return response;
        });
    }

    function handleScroll(lastSlide?: boolean) {
      if (driveStore.search) {
        if (pageIdx.value >= 2) {
          return;
        }
        onScroll(fetchSearchData, DIRECTION.BOTTOM, dataLoading.value);
      } else {
        if (casesCurrentPage.value >= 2 || casesCurrentPage.value <= 1) {
          return;
        }
        onCacheScroll(fetchCacheData, DIRECTION.BOTTOM, casesLastPage.value, casesCurrentPage.value, false, lastSlide);
      }
    }

    function onCaseClick(iCase: ICase) {
      driveStore.resetCurrentCase();
      driveStore.currentCase.title = iCase.title;
      driveStore.currentCase.photosAmount = iCase.photosAmount;
      driveStore.currentCase.filesAmount = iCase.filesAmount;

      if (props.isNetwork) {
        router.push(`/drive/case/network/${iCase.id}/view/`);
      } else {
        router.push(`/drive/case/my/${iCase.id}/view/`);
      }
    }

    function viewAllCases() {
      if (props.isNetwork) {
        router.push('/drive/network/cases');
      } else {
        router.push('/drive/my/cases');
      }
    }

    function createCase() {
      router.push('/drive/case/create?userFolder=true');
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      pageIdx.value = 0;
      dataLoading.value = !!newSearch;
      driveStore.dataLoading.cases = true;
      timeout = setTimeout(() => {
        fetchSearchData()
          .then((response) => {
            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          })
          .finally(() => {
            dataLoading.value = false;
            driveStore.dataLoading.cases = false;
          });
      }, 1000);
    });

    return {
      mainStore,
      driveStore,
      dataLoading,
      cases,
      scroller,
      isPageLoading,
      handleScroll,
      onCaseClick,
      viewAllCases,
      createCase,
    };
  },
});
